import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
    type SaladAiAnswer,
    type SaladAiRecommendations,
    type SaladAiSurvey
} from '@lib/entities/SaladAi';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

/**
 * Redux twin of lib/state/SaladAi.ts + the async work of lib/hooks/useSaladAI.ts (JSAD-7433).
 * The toasts `submitAnswers` shows move to the saga; the welcome-banner storage stays in the
 * hook.
 */
export interface SaladAiState {
    survey: SaladAiSurvey | null;
    answers: SaladAiAnswer[];
    recommendations: SaladAiRecommendations | null;
    surveyLoading: boolean;
    surveyError: string | null;
    submitLoading: boolean;
    submitError: string | null;
}

const initialState: SaladAiState = {
    survey: null,
    answers: [],
    recommendations: null,
    surveyLoading: false,
    surveyError: null,
    submitLoading: false,
    submitError: null
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

const saladAiSlice = createSlice({
    name: 'saladAi',
    initialState,
    reducers: {
        setSurvey: (state, action: PayloadAction<SaladAiSurvey>) => {
            state.survey = action.payload;
        },
        setRecommendations: (state, action: PayloadAction<SaladAiRecommendations>) => {
            state.recommendations = action.payload;
        },
        setSurveyLoading: (state, action: PayloadAction<boolean>) => {
            state.surveyLoading = action.payload;
        },
        setSurveyError: (state, action: PayloadAction<string | null>) => {
            state.surveyError = action.payload;
        },
        setSubmitLoading: (state, action: PayloadAction<boolean>) => {
            state.submitLoading = action.payload;
        },
        setSubmitError: (state, action: PayloadAction<string | null>) => {
            state.submitError = action.payload;
        },
        answerQuestion: (state, action: PayloadAction<SaladAiAnswer>) => {
            const newAnswer = action.payload;
            const currentAnswers = state.answers || [];
            const existingAnswerIndex = currentAnswers.findIndex(
                (answer) => answer.questionId === newAnswer.questionId
            );

            if (existingAnswerIndex === -1 && newAnswer.selections.length > 0) {
                state.answers = [...currentAnswers, newAnswer];
                return;
            }

            if (newAnswer.selections.length === 0) {
                state.answers = currentAnswers.filter((_, index) => index !== existingAnswerIndex);
            } else {
                state.answers = currentAnswers.map((answer, index) =>
                    index === existingAnswerIndex
                        ? { ...answer, selections: newAnswer.selections }
                        : answer
                );
            }
        },
        answerQuestionWithNoneSelected: (state, action: PayloadAction<number>) => {
            const questionId = action.payload;
            const currentAnswers = state.answers || [];
            const existingAnswerIndex = currentAnswers.findIndex(
                (answer) => answer.questionId === questionId
            );

            if (existingAnswerIndex === -1) {
                state.answers = [...currentAnswers, { questionId, selections: [] }];
                return;
            }

            state.answers = currentAnswers.map((answer, index) =>
                index === existingAnswerIndex ? { ...answer, selections: [] } : answer
            );
        },
        resetRecommendations: (state) => {
            state.recommendations = null;
        },
        resetAnswers: (state) => {
            state.answers = [];
        },
        /** `SaladAi.ts:104` — resets everything EXCEPT the loaded `survey`. Ported as-is. */
        resetSaladAi: (state) => {
            state.answers = [];
            state.recommendations = null;
            state.surveyLoading = false;
            state.surveyError = null;
            state.submitLoading = false;
            state.submitError = null;
        },

        // ── Saga-only requests (useSaladAI async work) ─────────────────────────────────────
        fetchSurveyRequest: request<void>(),
        submitAnswersRequest: request<void>()
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetSaladAi()` (useAuth.ts:117).
        builder.addCase(loggedOut, (state) => {
            state.answers = [];
            state.recommendations = null;
            state.surveyLoading = false;
            state.surveyError = null;
            state.submitLoading = false;
            state.submitError = null;
        });
    }
});

export const {
    setSurvey,
    setRecommendations,
    setSurveyLoading,
    setSurveyError,
    setSubmitLoading,
    setSubmitError,
    answerQuestion,
    answerQuestionWithNoneSelected,
    resetRecommendations,
    resetAnswers,
    resetSaladAi,
    fetchSurveyRequest,
    submitAnswersRequest
} = saladAiSlice.actions;

export const selectSaladAi = (state: { saladAi: SaladAiState }) => state.saladAi;
export const selectSaladAiAnswers = (state: { saladAi: SaladAiState }) => state.saladAi.answers;

export default saladAiSlice.reducer;
